// "Waiting on you": turns the overview aggregate into the curator's task
// list, oldest first. Pure, so the wording and ordering are testable without
// rendering.
import {
  EMPLOYMENT_LABELS,
  SENIORITY_LABELS,
  label,
  plural,
  relativeDate,
} from '@remote-medium/shared';
import {
  Building2,
  CalendarClock,
  ListChecks,
  ListPlus,
  Mail,
  MessageSquarePlus,
  Ticket,
  Users,
  type LucideIcon,
} from 'lucide-react';

import type { Overview } from './context.ts';

type QueueRow = Overview['queue'][number];

export interface Task {
  key: string;
  icon: LucideIcon;
  title: string;
  detail: string;
  action: string;
  to: string;
  /** Oldest thing waiting in this task; drives "oldest first". */
  since: string;
}

// "Title · Senior".
const withMeta = (title: string, meta: string) => `${title} · ${meta}`;
const roleName = (r: QueueRow) => withMeta(r.title, label(SENIORITY_LABELS, r.seniority));
const minIso = (xs: (string | null)[], now: Date) =>
  xs.filter((x): x is string => !!x).sort()[0] ?? now.toISOString();

/** Up to three "<role> has N …" clauses, then "and N more roles". */
function perRole(rows: QueueRow[], clause: (r: QueueRow) => string) {
  const shown = rows.slice(0, 3).map(clause);
  const rest = rows.length - shown.length;
  return `${shown.join('. ')}${rest > 0 ? `. And ${plural(rest, 'more role')}` : ''}.`;
}

/** A role link when the task is about one role, else the roles list. */
const roleLink = (rows: QueueRow[]) => (rows.length === 1 ? `/roles/${rows[0]!.roleId}` : '/roles');

function screenTask(d: Overview, now: Date): Task | null {
  const fresh = d.newCandidates;
  if (!fresh.count) return null;
  const joined = fresh.joinedAt
    .map((at) => relativeDate(at, now))
    .join(fresh.joinedAt.length === 2 ? ' and ' : ', ');
  return {
    key: 'screen',
    icon: Users,
    title: `Screen ${plural(fresh.count, 'new candidate')}`,
    detail: `Joined ${joined}${fresh.count > 3 ? ' and later' : ''}. Until screened they aren't ranked against any role.`,
    action: 'Open queue',
    to: '/candidates?status=new',
    since: minIso(fresh.joinedAt, now),
  };
}

function reviewTask(d: Overview, now: Date): Task | null {
  const rows = d.queue.filter((r) => r.review > 0);
  if (!rows.length) return null;
  const n = rows.reduce((s, r) => s + r.review, 0);
  return {
    key: 'review',
    icon: ListChecks,
    title: `Review ${plural(n, 'ranked match', 'ranked matches')}`,
    detail: perRole(
      rows,
      (r) =>
        `${roleName(r)} has ${r.review}${r.review > 1 ? `, top score ${r.reviewTopScore}` : `, score ${r.reviewTopScore}`}`,
    ),
    action: 'Confirm or reject',
    to: roleLink(rows),
    since: minIso(
      rows.map((r) => r.reviewSince),
      now,
    ),
  };
}

// Confirmed but never asked, including matches confirmed after the role was
// already shortlisted.
function shortlistTask(d: Overview, now: Date): Task | null {
  const rows = d.queue.filter((r) => r.ask > 0);
  if (!rows.length) return null;
  return {
    key: 'shortlist',
    icon: Mail,
    title: `Submit ${plural(rows.length, 'shortlist')}`,
    detail: `${perRole(rows, (r) => `${roleName(r)} has ${plural(r.ask, 'confirmed match', 'confirmed matches')} not yet asked`)} Consent links expire after ${d.workflow.consentTokenDays} days.`,
    action: 'Ask candidates',
    to: roleLink(rows),
    since: minIso(
      rows.map((r) => r.askSince),
      now,
    ),
  };
}

// Said yes and either the company reacted interested, or it hasn't reacted
// within the reaction window (the server applies the window).
function introTask(d: Overview, now: Date, billing: boolean): Task | null {
  const rows = d.queue.filter((r) => r.intro > 0);
  if (!rows.length) return null;
  const n = rows.reduce((s, r) => s + r.intro, 0);
  const days = plural(d.workflow.reactionWindowDays, 'day');
  return {
    key: 'intro',
    icon: Ticket,
    title: `Send ${plural(n, 'introduction')}`,
    detail: perRole(rows, (r) => {
      const who = r.intro === 1 ? 'A candidate said yes' : `${r.intro} candidates said yes`;
      const reaction =
        r.introInterested === r.intro
          ? 'the company reacted interested'
          : r.introInterested === 0
            ? `the company hasn't reacted in ${days}`
            : `the company reacted interested to ${r.introInterested}, the rest are past the ${days} window`;
      // Rule 4: only the first introduction for a role uses a credit.
      // Paused billing (Q65): nothing is charged.
      const cost = !billing
        ? 'Free while billing is off'
        : r.status === 'introduced'
          ? 'No credit needed; the role already has an introduction'
          : `Uses 1 role credit; they have ${r.creditsRemaining} left`;
      return `${who} on ${roleName(r)} and ${reaction}. ${cost}`;
    }),
    action: 'Review and send',
    to: roleLink(rows),
    since: minIso(
      rows.map((r) => r.introSince),
      now,
    ),
  };
}

function staleTask(d: Overview, now: Date): Task | null {
  const { count, oldest, since } = d.staleCurated;
  if (!count || !oldest) return null;
  const name = withMeta(oldest.title, label(EMPLOYMENT_LABELS, oldest.employmentType));
  const checked = oldest.lastSeenAt
    ? `was last checked ${relativeDate(oldest.lastSeenAt, now)}`
    : 'has never been checked';
  return {
    key: 'stale',
    icon: CalendarClock,
    title: `Re-verify ${plural(count, 'curated listing')}`,
    detail:
      count === 1
        ? `${name} ${checked}.`
        : `Oldest: ${name} ${checked}. ${plural(count - 1, 'other')} past the freshness window.`,
    action: 'Check listing',
    to: count === 1 ? `/roles/${oldest.id}` : '/roles?kind=stale',
    since: minIso([since], now),
  };
}

function suggestionsTask(d: Overview, now: Date): Task | null {
  const { count, since } = d.pendingSkills;
  if (!count) return null;
  return {
    key: 'suggestions',
    icon: MessageSquarePlus,
    title: `Review ${plural(count, 'new skill')}`,
    detail: 'Companies and candidates added skills that are in use but not approved yet.',
    action: 'Approve or reject',
    to: '/suggestions',
    since: minIso([since], now),
  };
}

// Role types companies and candidates added themselves (Q77); already in use.
function roleTypesTask(d: Overview, now: Date): Task | null {
  const { count, since } = d.pendingRoleTypes;
  if (!count) return null;
  return {
    key: 'role-types',
    icon: ListPlus,
    title: `Review ${plural(count, 'new role type')}`,
    detail: 'Added by companies or candidates and already in use. Keep, replace or reject.',
    action: 'Review',
    to: '/suggestions?tab=role-types',
    since: minIso([since], now),
  };
}

function approvalsTask(d: Overview, now: Date, billing: boolean): Task | null {
  const { count, first, since } = d.companyApprovals;
  if (!count) return null;
  return {
    key: 'companies',
    icon: Building2,
    title: `Approve ${plural(count, 'company', 'companies')}`,
    detail: `${first.map((c) => c.name).join(', ')}${count > 3 ? ' and others' : ''} ${count === 1 ? 'is' : 'are'} waiting for ${billing ? 'the curator sanity check' : 'you to activate them'}.`,
    action: 'Review account',
    to: count === 1 ? `/companies/${first[0]!.id}` : '/companies',
    since: minIso([since], now),
  };
}

/**
 * Every task with something waiting, oldest first, plus the always-shown
 * queues that are empty ("Also clear: …").
 */
export function buildTasks(
  d: Overview,
  now = new Date(),
  billing = true,
): { tasks: Task[]; clear: string[] } {
  const tasks = [
    screenTask(d, now),
    reviewTask(d, now),
    shortlistTask(d, now),
    introTask(d, now, billing),
    staleTask(d, now),
    suggestionsTask(d, now),
    roleTypesTask(d, now),
    approvalsTask(d, now, billing),
  ].filter((t): t is Task => t !== null);

  const clear: string[] = [];
  if (!d.pendingSkills.count) clear.push('new skills (0)');
  if (!d.pendingRoleTypes.count) clear.push('new role types (0)');
  if (!d.companyApprovals.count) clear.push('company approvals (0)');

  tasks.sort((a, b) => a.since.localeCompare(b.since));
  return { tasks, clear };
}
